import React from 'react';
import { Tooltip, useTheme } from '@mui/material';
import { ResponsiveContainer, Pie, PieChart } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import ArcChartTooltip from 'components/widgets/charts/ArcChart/ArcChartTooltip'

export interface ArcChartProps {
  // percentage represented as a decimal value between 0 and 1
  percentage: number;
  value?: number;

  fieldLabel?: string;

  // The color of the value and background
  color?: string;
  backgroundColor?: string;
}

export default function ArcChart(props: ArcChartProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [chartSize, setChartSize] = React.useState<number>(100);
  const [hoveredArc, setHoveredArc] = React.useState<any>(null);

  const color = props.color || theme.palette.primary.main;
  const backgroundColor = props.backgroundColor || theme.palette.grey[300];

  const arcData = [{
    name: i18n.t("other"),
    value: props.value === undefined ? ((1 - props.percentage) * 100) : 
            props.percentage !== null && props.percentage > 0 ? Math.round((props.value / props.percentage) - props.value) : 0,
    fill: backgroundColor
  }, {
    name: props.fieldLabel || i18n.t("value"),
    value: props.value === undefined ? props.percentage * 100 : props.value,
    fill: color
  }];
  
  const isEmpty = !props.value;
  const emptyArcData = [{
    value: 100,
    fill: backgroundColor
  }];

  return (
    <Tooltip 
      title={(hoveredArc) ? <ArcChartTooltip arc={hoveredArc} /> : ""}
      open={Boolean(hoveredArc)}
      followCursor={true}
    >
      <Box sx={{position:"relative", height: "100%"}}>
        <ResponsiveContainer
          aspect={1}
          height="100%"
          onResize={(width, height) => {
            setChartSize(Math.min(width, height));
          }}>
          <PieChart style={{cursor: "pointer"}}>
            {!isEmpty && 
              <Pie 
                data={arcData} 
                dataKey="value" 
                nameKey="name" 
                cx="50%" 
                cy="50%" 
                innerRadius="60%" 
                outerRadius="90%" 
                startAngle={90} 
                endAngle={450}
                onMouseEnter={(arc) => {
                  setHoveredArc(arc);
                }}
                onMouseLeave={() => {
                  setHoveredArc(null);
                }}
              />
            || 
              <Pie 
                data={emptyArcData} 
                dataKey="value" 
                nameKey="name" 
                cx="50%" 
                cy="50%" 
                innerRadius="60%" 
                outerRadius="90%" 
                startAngle={90} 
                endAngle={450}
                onMouseEnter={(arc) => {
                  setHoveredArc(arc);
                }}
                onMouseLeave={() => {
                  setHoveredArc(null);
                }}
              />
            }
            <text x='50%' y='50%' dy={chartSize * 0.06} style={{ fontSize: chartSize * 0.2, fontWeight: 'bold', fill: color }} textAnchor='middle'>
              {Math.round(props.percentage * 100) + "%"}
            </text>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Tooltip>
  );
}
